import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PlaycastContext } from '../PlaycastWrapper';
import ReactJson from 'react-json-view';
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@playcastdotio/Shadcn';
import constants from '@playcastdotio/GlobalConfig';
import { PresetControls } from './PresetControls';
import { AfkPreventionControls } from './AfkPreventionControls';
import { ShadowDisplayControl } from './ShadowDisplayControl';
import { BitrateControl } from './BitrateControl';
import { CameraResolutionControl } from './CameraResolutionControl';
import { EncoderConfig } from '@playcastdotio/CoreTypes';
import { performanceModeState } from '../../api/stores/shared';
import { GameInstanceDevControls } from './GameInstanceDevControls';

export const DevData = () => {
  const context = useContext(PlaycastContext);
  const { stateStores } = context;
  const { apiState, hostState } = stateStores;
  const [remoteUrl, setRemoteUrl] = useState<string>(apiState.remoteUrl);
  const [selectedRegion, setSelectedRegion] = useState<string | undefined>(context.stateStores.regionOverrideState.regionOverride);

  // Add new state for process management
  const [processPolicy, setProcessPolicy] = useState({
    nonFocusInput: false,
    constrainMouse: true,
    allowToolbarAccess: true,
    forceProcessOnTop: false,
    launchTimeout: 60000,
    captureAudio: true,
    captureVideo: true,
    saveEnumeratedApps: true,
    onlyExitOnDisconnect: true,
  });
  const [selectedApp, setSelectedApp] = useState<string>('');

  const [enableHostDominanceKick, setHostDominanceKick] = useState(false);

  useEffect(() => {
    setSelectedRegion(context.stateStores.regionOverrideState.regionOverride);
  }, [context.stateStores.regionOverrideState.regionOverride]);

  // Region override options
  const regionOptions = [
    { label: 'Clear Region Override', value: 'undefined' },
    { label: 'Auto', value: 'auto' },
    { label: 'US East 1', value: 'us-east-1' },
    { label: 'US East 2', value: 'us-east-2' },
    { label: 'US West 1', value: 'us-west-1' },
    { label: 'US West 2', value: 'us-west-2' },
  ];

  // Function to refresh running applications
  const refreshRunningApplications = () => {
    context.targetFunctions.processEnumRunning();
  };

  const setEncoderConfig = async () => {
    const config: EncoderConfig = {
      ...context.stateStores.gameClipConfigState,
      enabled: true,
      recordHotkey: 'Ctrl-Alt-R',
      bufferDurationSeconds: 10,
      cameraName: 'ShadowCam03',
    };
    context.targetFunctions.setEncoderConfig(config);
  };

  const handleSelectApp = (processId: number) => {
    // set process policy
    context.targetFunctions.processSetPolicy(
      processPolicy.nonFocusInput,
      processPolicy.constrainMouse,
      processPolicy.allowToolbarAccess,
      processPolicy.forceProcessOnTop,
      processPolicy.launchTimeout,
      processPolicy.captureAudio,
      processPolicy.captureVideo,
      processPolicy.saveEnumeratedApps,
      processPolicy.onlyExitOnDisconnect,
    );

    // select app
    context.targetFunctions.processSelect(processId);
  };

  const handleForceMatchmakeable = () => {
    context.electronFunctions?.forceMatchmakeable();
  };

  // Effect to update process policy
  useEffect(() => {
    if (context.electronFunctions) {
      // context.electronFunctions.sendMessage({
      //   target: 'process',
      //   action: 'setPolicy',
      //   isReply: false,
      //   message: processPolicy,
      // });
    }
  }, [processPolicy, context.electronFunctions]);

  const handleRemoteUrlSave = () => {
    context.dev?.setApiRemoteUrl(remoteUrl);
  };

  const handleRegionOverrideChange = (value: string) => {
    if (value === 'undefined') {
      setSelectedRegion(undefined);
      context.dev?.updateRegionOverride(undefined);
    } else {
      setSelectedRegion(value);
      context.dev?.updateRegionOverride(value);
    }
  };

  const handleCopyStateStores = () => {
    // multiline string for template
    // make a copy of the stateStores, remove auth from it and replace it with a string
    const isAnonAuth = context.stateStores.authState.accessToken.startsWith('hpl;');
    const replacementAuthState = {
      ...context.stateStores.authState,
      accessToken: isAnonAuth ? 'hpl;<redacted>' : '<redacted>',
    };
    const replacementLoginState = {
      ...context.stateStores.loginProfileState,
      accessToken: context.stateStores.loginProfileState.accessToken ? (isAnonAuth ? 'hpl;<redacted>' : '<redacted>') : '',
    };
    const stateStoresCopy = {
      ...context.stateStores,
      authState: replacementAuthState,
      loginProfileState: replacementLoginState,
    };

    const debugDump = `
---

<details>
<summary>
<h3>Debug Dump: Host</h3>
</summary>

\`\`\`json
${JSON.stringify(stateStoresCopy, null, 2)}
\`\`\`
</details>

---
`;
    navigator.clipboard.writeText(debugDump);
  };

  const handleReconnectPogLogClick = () => {
    context.dev?.reconnectMessageCaptureClient();
  };

  const handleWebrtcInternalsClick = () => {
    context.dev?.showWebrtcInternals();
  };

  const handleGpuInternalsClick = () => {
    context.dev?.showGpuInternals();
  };

  const handleToggleVideoPreview = () => {
    context.targetFunctions.showVideoPreview();
  };

  const handleSteamInstall = (steamInstall: boolean) => {
    context.electronFunctions?.setSteamInstall(steamInstall);
  };

  const handleTouchInputEnabled = (enabled: boolean) => {
    console.log('[DevData] Touch Input button clicked:', { enabled });
    context.electronFunctions?.setTouchInputEnabled(enabled);
    context.targetFunctions.setAllowedTouchInput(enabled);

    console.log('[DevData] Updated touch state:', {
      touchInputConfigState: enabled,
      globalInputState: enabled
    });
  };

  const toggleHardKick = () => {
    const nextEnabled = !enableHostDominanceKick;
    setHostDominanceKick(nextEnabled);
    context.targetFunctions.processSetPolicy(
      processPolicy.nonFocusInput,
      processPolicy.constrainMouse,
      processPolicy.allowToolbarAccess,
      processPolicy.forceProcessOnTop,
      processPolicy.launchTimeout,
      processPolicy.captureAudio,
      processPolicy.captureVideo,
      processPolicy.saveEnumeratedApps,
      processPolicy.onlyExitOnDisconnect,
      nextEnabled,
    );
  };

  const handleEnablePerformanceMode = () => {
    console.log('[Performance Mode] Manually enabled from DevData');
    performanceModeState.updateState({ enabled: true });
    localStorage.setItem('performanceMode', 'true');
  };

  const handlePlayjectorBigPicture = () => {
    context.dev?.launchBigPictureMode();
  };

  const handlePlayjectorStopPavilion = () => {
    context.dev?.stopPlayjectorPavilion();
  };

  const [shouldShow, setShouldShow] = useState(false);
  const savedBoundsRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  // Disconnect history table state and helpers
  type DisconnectRecord = {
    at: string;
    reason: string;
    context?: string;
  };
  type GroupedRow = {
    minuteKey: string;
    entries: DisconnectRecord[];
  };
  const STORAGE_KEY = 'playcast.peer.disconnectHistory';
  const readHistory = (): DisconnectRecord[] => {
    try {
      if (typeof window === 'undefined' || !('localStorage' in window)) return [];
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed: DisconnectRecord[] = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };
  const formatMinuteKey = (isoOrHuman: string): string => {
    const d = new Date(isoOrHuman);
    if (!isNaN(d.getTime())) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    }
    return isoOrHuman.slice(0, 16);
  };
  const [disconnectRecords, setDisconnectRecords] = useState<DisconnectRecord[]>(readHistory());
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setDisconnectRecords(readHistory());
    };
    window.addEventListener('storage', onStorage);
    const interval = setInterval(() => setDisconnectRecords(readHistory()), 1500);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);
  const groupedDisconnects = useMemo<GroupedRow[]>(() => {
    const byMinute: Record<string, DisconnectRecord[]> = {};
    for (const rec of disconnectRecords) {
      const key = formatMinuteKey(rec.at);
      byMinute[key] = byMinute[key] || [];
      byMinute[key].push(rec);
    }
    return Object.entries(byMinute)
      .map(([minuteKey, entries]) => ({ minuteKey, entries }))
      .sort((a, b) => (a.minuteKey < b.minuteKey ? 1 : -1));
  }, [disconnectRecords]);
  const clearDisconnectHistory = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setDisconnectRecords([]);
    } catch (err) {
      console.error('[Host DevData] Failed to clear disconnect history', err);
    }
  };

  // Move keydown event listener into an effect with cleanup
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F12' && e.ctrlKey) {
        if (constants.ENVIRONMENT === 'development' || constants.ENVIRONMENT === 'preview') {
          setShouldShow((prev) => !prev);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup function to remove the event listener when component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []); // Empty dependency array means this effect runs once on mount

  useEffect(() => {
    const wc = window.windowControls;
    if (!wc) return;

    if (shouldShow) {
      void (async () => {
        try {
          const cur = await wc.getBounds();
          savedBoundsRef.current = cur;
          const DEV_W = 1200;
          const DEV_H = 800;
          const nx = Math.round((window.screen.availWidth - DEV_W) / 2);
          const ny = Math.round((window.screen.availHeight - DEV_H) / 2);
          await wc.setBounds({ x: nx, y: ny, width: DEV_W, height: DEV_H });
        } catch (e) {
          console.warn('[DevData] window resize failed', e);
        }
      })();
    } else if (savedBoundsRef.current) {
      const bounds = savedBoundsRef.current;
      savedBoundsRef.current = null;
      void (async () => {
        try {
          await wc.resetPavilionBounds?.(bounds);
        } catch (e) {
          console.warn('[DevData] window restore failed', e);
        }
      })();
    }
  }, [shouldShow]);

  return (
    shouldShow && (
      <div className='fixed inset-0 z-[9999] overflow-auto bg-black/90 p-4 flex flex-col space-y-6 text-white'>
        <div className='flex flex-col space-y-1'>
          <h3>Automatic Updates</h3>
          <div className='flex space-x-4'>
            <Button onClick={context.targetFunctions.getAutomaticUpdatesEnabled}>Get Automatic Updates Enabled</Button>
            <Button onClick={() => context.targetFunctions.setAutomaticUpdatesEnabled(true)}>Set Automatic Updates Enabled</Button>
            <Button onClick={() => context.targetFunctions.setAutomaticUpdatesEnabled(false)}>Set Automatic Updates Disabled</Button>
          </div>
        </div>
        <div className='flex flex-col space-y-1'>
          <h3>API Remote URL</h3>
          <div className='flex space-x-4'>
            <Input value={remoteUrl} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRemoteUrl(e.target.value)} />
            <Button
              disabled={remoteUrl === apiState.remoteUrl}
              variant='ghost'
              className='text-primary hover:bg-inherit hover:text-yellow-500 disabled:text-gray-400'
              onClick={handleRemoteUrlSave}
            >
              Save Changes
            </Button>
          </div>
        </div>
        <div className='flex flex-col space-y-1'>
          <h3>Region Override</h3>
          <div className='flex space-x-4'>
            <Select value={selectedRegion} onValueChange={handleRegionOverrideChange}>
              <SelectTrigger className='w-full max-w-xs'>
                <SelectValue placeholder='Select a region' />
              </SelectTrigger>
              <SelectContent>
                {regionOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {context.remoteControlFunctions && (
          <Button className='m-0' onClick={handleToggleVideoPreview}>
            Toggle Preview
          </Button>
        )}
        {context.electronFunctions && (
          <div>
            <div className='my-2 flex flex-col space-y-1'>
              <div className='flex flex-row space-x-1'>
                <Button className='m-0' onClick={handleReconnectPogLogClick}>
                  Connect Message Capture
                </Button>

                <Button className='m-0' onClick={handleWebrtcInternalsClick}>
                  Open WebRTC Internals
                </Button>
                <Button className='m-0' onClick={handleGpuInternalsClick}>
                  GPU Stats
                </Button>
                <Button
                  className='m-0 bg-red-500 hover:bg-red-600 focus:bg-red-500 active:bg-red-600'
                  onClick={context.electronFunctions?.killPlayjector}
                >
                  Kill Playjector
                </Button>
                <Button
                  className='m-0 bg-green-500 hover:bg-green-600 focus:bg-green-600 active:bg-green-700'
                  onClick={context.electronFunctions?.connectPlayjector}
                >
                  Connect Playjector
                </Button>
              </div>
              <div className='flex flex-row space-x-1'>
                <Button className='m-0' onClick={setEncoderConfig}>
                  Set Encoder Config
                </Button>
                <Button className='m-0' onClick={context.targetFunctions.toggleEncoder}>
                  Toggle Encoder
                </Button>
                <Button className='m-0' onClick={handleForceMatchmakeable}>
                  Matchmakeable True
                </Button>
                <Button className='m-0' onClick={() => handleSteamInstall(true)}>
                  Steam Install True
                </Button>
                <Button className='m-0' onClick={() => handleSteamInstall(false)}>
                  Steam Install False
                </Button>
                <Button className='m-0 bg-amber-600 hover:bg-amber-700' onClick={handleEnablePerformanceMode}>
                  Enable Performance Mode
                </Button>
              </div>
              <div className='flex flex-row space-x-1'>
                <Button
                  className={`m-0 ${context.stateStores.globalInputState.touch ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'}`}
                  onClick={() => handleTouchInputEnabled(!context.stateStores.globalInputState.touch)}
                >
                  Touch Input: {context.stateStores.globalInputState.touch ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              <div className='flex flex-row space-x-1'>
                <Button className='m-0' onClick={() => handlePlayjectorBigPicture()}>
                  Launch Big Picture
                </Button>
                <Button className='m-0' onClick={() => handlePlayjectorStopPavilion()}>
                  Stop Playjector Pavilion
                </Button>
                <Button className='m-0' onClick={() => toggleHardKick()}>
                  Toggle HostDominance
                </Button>
              </div>
            </div>
          </div>
        )}

        <PresetControls />

        <BitrateControl />

        <AfkPreventionControls />

        <ShadowDisplayControl />

        <CameraResolutionControl />

        <GameInstanceDevControls />

        {/* Disconnect History */}
        <div className='flex flex-col space-y-3 rounded-lg border border-gray-700 p-4'>
          <div className='mb-1 flex items-center justify-between'>
            <h3 className='text-lg font-semibold'>Disconnect History</h3>
            <Button className='m-0 bg-red-600 hover:bg-red-700' onClick={clearDisconnectHistory}>
              Clear
            </Button>
          </div>
          {groupedDisconnects.length === 0 ? (
            <div className='text-gray-400'>No disconnects recorded yet.</div>
          ) : (
            <div className='space-y-3'>
              {groupedDisconnects.map((row) => (
                <div key={row.minuteKey} className='overflow-hidden rounded border border-gray-700 bg-neutral-900 text-neutral-100'>
                  <div className='bg-neutral-800 px-3 py-2 text-sm font-medium'>
                    {row.minuteKey} ({row.entries.length})
                  </div>
                  <div className='divide-y divide-neutral-800'>
                    {row.entries.map((e, idx) => (
                      <div key={idx} className='grid grid-cols-1 gap-2 px-3 py-2 sm:grid-cols-12 sm:items-center'>
                        <div className='text-xs text-neutral-400 sm:col-span-3'>{e.at}</div>
                        <div className='break-words text-sm sm:col-span-6'>{e.reason}</div>
                        <div className='break-words text-xs text-neutral-400 sm:col-span-3'>{e.context || ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='flex flex-col space-y-4 rounded-lg border border-gray-700 p-4'>
          <h3 className='text-lg font-semibold'>Process Management</h3>

          <Select
            value={selectedApp}
            onValueChange={(value) => {
              setSelectedApp(value);
              const app = stateStores.runningApplicationState.find((app) => app[0] === value);
              if (app) {
                handleSelectApp(app[1].processId);
              }
            }}
            onOpenChange={(open) => {
              if (open) {
                refreshRunningApplications();
              }
            }}
          >
            <SelectTrigger className='w-full'>
              <SelectValue placeholder='Select an application' />
            </SelectTrigger>
            <SelectContent>
              {stateStores.runningApplicationState.map((app) => (
                <SelectItem key={app[0]} value={app[0]}>
                  {app[0]} (PID: {app[1].processId})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className='flex flex-row space-x-1'>
          <Button className='m-0' onClick={handleCopyStateStores}>
            Dump Data to Clipboard
          </Button>
        </div>
        <ReactJson
          name={false}
          src={context.stateStores}
          theme={'harmonic'}
          collapseStringsAfterLength={80}
          collapsed={1}
          indentWidth={2}
          style={{ fontSize: '16px' }}
        />
      </div>
    )
  );
};
